import React from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, Plus, Edit2, X, Building2, Mail, Phone, Trash2 } from 'lucide-react';

import { LoadingSpinner, PageLoadingState } from '../components/LoadingSpinner';
import { AppTopBar } from '../components/AppTopBar';
import { AppSidebar } from '../components/AppSidebar';
import { AppConfirmDialog } from '../components/AppConfirmDialog';
import { AddressAutocompleteInput } from '../components/AddressAutocompleteInput';
import { Contact, Organization, contactDisplayName, type Lead } from '../types';
import {
  contactsHubPath,
  organizationDetailPath,
} from '../utils/contactRoutes';
import { leadDetailPath, projectsListPath } from '../utils/leadRoutes';
import { contactAvatarClassName } from '../utils/entityAvatarClasses';
import { CreateLeadModal } from '../features/leads/components/CreateLeadModal';
import { CustomerPartySummary } from '../features/leads/components/CustomerPartySummary';
import { ComposeEmailModal } from '../features/inbox/components/ComposeEmailModal';
import { fetchDispatchAccount } from '../utils/mailDispatchApi';
import { InvoiceDocumentsList } from '../features/contacts/InvoiceDocumentsList';
import { LeadProjectsSection } from '../features/contacts/LeadProjectsSection';
import { ContactKindBadgePicker } from '../features/contacts/ContactKindBadgePicker';
import { OrganizationAssignSelect } from '../features/contacts/OrganizationAssignSelect';
import { useDocumentTitle } from '../hooks/useDocumentTitle';
import { useNavigateBack } from '../hooks/useNavigateBack';
import { useModuleAccess } from '../hooks/useModuleAccess';
import { APP_BTN_OUTLINE_CLASS, APP_BTN_PRIMARY_CLASS, FILTER_GROUP_LABEL_CLASS } from '../ui/controlStyles';

type LeadLike = {
  id: string;
  display_id?: string | null;
  name?: string;
  lead_title?: string | null;
  status?: string;
  category?: string;
  created_at?: string;
  contact_id?: string | null;
  site_address?: string | null;
  offer_net_total?: number | null;
  offer_gross_total?: number | null;
  contact_roles?: Array<'project_contact' | 'site_contact'>;
};

type InvoiceDoc = {
  id: string;
  lead_id: string | null;
  direction: 'outgoing' | 'incoming';
  document_type: string;
  invoice_number: string | null;
  filename: string;
  issue_date: string | null;
  due_date?: string | null;
  paid_at?: string | null;
  net_amount: number | null;
  gross_amount: number | null;
  status: string;
  lead_display_id?: string | null;
  lead_title?: string | null;
  created_at: string;
};

const formatCurrency = (v: number | null | undefined): string =>
  v == null
    ? '—'
    : new Intl.NumberFormat('de-DE', { style: 'currency', currency: 'EUR' }).format(Number(v) || 0);

const formatDateDe = (s: string | null | undefined): string =>
  s ? new Date(s).toLocaleDateString('de-DE') : '—';

function contactInitials(c: Contact): string {
  const fn = (c.first_name ?? '').trim();
  const ln = (c.last_name ?? '').trim();
  if (fn && ln) return `${fn[0]}${ln[0]}`.toUpperCase();
  const dn = contactDisplayName(c).trim();
  if (!dn) return '?';
  const parts = dn.split(/\s+/).filter(Boolean);
  if (parts.length >= 2) return `${parts[0][0]}${parts[parts.length - 1][0]}`.toUpperCase();
  return dn.slice(0, 2).toUpperCase();
}

async function parseApiError(res: Response, fallback: string): Promise<string> {
  try {
    const data = (await res.json()) as { error?: string };
    if (typeof data?.error === 'string' && data.error.trim()) return data.error;
  } catch {
    /* ignore */
  }
  return fallback;
}

export const ContactDetailPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { canOpen, loaded: modulesLoaded, isEnabled } = useModuleAccess();
  // Projekte und Rechnungen des Kontakts kommen über Quer-Routen, die das jeweilige Modul brauchen.
  const projectsEnabled = modulesLoaded && isEnabled('projekte');
  const invoicesEnabled = modulesLoaded && isEnabled('rechnungen');
  const goBack = useNavigateBack(contactsHubPath('contacts'));

  const [contact, setContact] = React.useState<Contact | null>(null);
  const [organization, setOrganization] = React.useState<Organization | null>(null);
  const [leads, setLeads] = React.useState<LeadLike[]>([]);
  const [invoices, setInvoices] = React.useState<InvoiceDoc[]>([]);
  const [loading, setLoading] = React.useState(true);
  const [editingSection, setEditingSection] = React.useState<'contact' | 'stammdaten' | 'notes' | null>(null);
  const [saving, setSaving] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);
  const [createLeadModalOpen, setCreateLeadModalOpen] = React.useState(false);
  const [emailCompose, setEmailCompose] = React.useState<{
    accountId: string | null;
    to: string;
    subject: string;
    body: string;
    fromEmail?: string | null;
  } | null>(null);
  const [emailComposeResetKey, setEmailComposeResetKey] = React.useState(0);
  const [emailComposeBusy, setEmailComposeBusy] = React.useState(false);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = React.useState(false);
  const [deleting, setDeleting] = React.useState(false);

  const contactDocumentTitle = React.useMemo(() => {
    if (!contact || !id || String(contact.id) !== String(id)) return null;
    return contactDisplayName(contact).trim() || null;
  }, [contact, id]);
  useDocumentTitle(contactDocumentTitle);

  const reload = React.useCallback((options?: { quiet?: boolean }) => {
    if (!id) return;
    const quiet = options?.quiet === true;
    if (!quiet) setLoading(true);
    Promise.all([
      fetch(`/api/contacts/${id}`).then((r) => (r.ok ? r.json() : null)),
      projectsEnabled ? fetch(`/api/contacts/${id}/leads`).then((r) => (r.ok ? r.json() : [])) : Promise.resolve([]),
      invoicesEnabled
        ? fetch(`/api/contacts/${id}/invoice-documents`).then((r) => (r.ok ? r.json() : []))
        : Promise.resolve([]),
    ])
      .then(([c, ls, inv]) => {
        const ct = c as Contact | null;
        setContact(ct);
        setLeads(Array.isArray(ls) ? ls : []);
        setInvoices(Array.isArray(inv) ? inv : []);
        if (ct?.organization_id) {
          fetch(`/api/organizations/${ct.organization_id}`)
            .then((r) => (r.ok ? r.json() : null))
            .then((o) => setOrganization(o as Organization | null))
            .catch(() => setOrganization(null));
        } else {
          setOrganization(null);
        }
      })
      .catch(() => setError('Kontakt konnte nicht geladen werden.'))
      .finally(() => {
        if (!quiet) setLoading(false);
      });
  }, [id, projectsEnabled, invoicesEnabled]);

  React.useEffect(() => {
    if (!modulesLoaded) return;
    reload();
  }, [reload, modulesLoaded]);

  const contactRevenueTotals = React.useMemo(() => {
    let gross = 0;
    let net = 0;
    let nGross = 0;
    let nNet = 0;
    for (const l of leads) {
      const g = l.offer_gross_total;
      const n = l.offer_net_total;
      if (g != null && Number.isFinite(Number(g))) {
        gross += Number(g);
        nGross++;
      }
      if (n != null && Number.isFinite(Number(n))) {
        net += Number(n);
        nNet++;
      }
    }
    return {
      grossSum: nGross > 0 ? gross : null,
      netSum: nNet > 0 ? net : null,
    };
  }, [leads]);

  const openEmailCompose = React.useCallback(async () => {
    if (!contact?.email || emailComposeBusy) return;
    setEmailComposeBusy(true);
    try {
      const dispatch = await fetchDispatchAccount('general');
      if (!dispatch.account_id && !dispatch.can_choose) {
        throw new Error('Kein sendbares Postfach eingerichtet.');
      }
      const recipientName = contactDisplayName(contact);
      setEmailCompose({
        accountId: dispatch.account_id,
        to: contact.email.trim(),
        subject: '',
        body: recipientName ? `Hallo ${recipientName},\n\n` : '',
        fromEmail: dispatch.email,
      });
      setEmailComposeResetKey((k) => k + 1);
    } catch (e) {
      window.alert(e instanceof Error ? e.message : String(e));
    } finally {
      setEmailComposeBusy(false);
    }
  }, [contact, emailComposeBusy]);

  if (loading) {
    return (
      <div className="h-screen min-h-0 flex overflow-hidden bg-app-bg font-sans">
        <AppSidebar active="contacts" />
        <main className="flex-1 min-h-0 flex flex-col min-w-0 overflow-hidden pb-16 md:pb-0">
          <AppTopBar title="Kontakt" />
          <PageLoadingState />
        </main>
      </div>
    );
  }

  if (!contact) {
    return (
      <div className="h-screen min-h-0 flex overflow-hidden bg-app-bg font-sans">
        <AppSidebar active="contacts" />
        <main className="flex-1 min-h-0 flex flex-col min-w-0 overflow-hidden pb-16 md:pb-0">
          <AppTopBar title="Kontakt" />
          <div className="flex-1 flex flex-col items-center justify-center gap-3 text-sm text-app-text-subtle px-4">
            <p>{error ?? 'Kontakt nicht gefunden.'}</p>
            <button onClick={() => navigate(contactsHubPath('contacts'))} className="btn-pipedrive-secondary">
              Zur Übersicht
            </button>
          </div>
        </main>
      </div>
    );
  }

  const handleSave = async (patch: Partial<Contact>, opts?: { keepEditing?: boolean }): Promise<boolean> => {
    setSaving(true);
    setError(null);
    try {
      const res = await fetch(`/api/contacts/${id}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(patch),
      });
      if (!res.ok) {
        const body = (await res.json().catch(() => ({}))) as { error?: string };
        throw new Error(body.error ?? 'Update fehlgeschlagen.');
      }
      const updated = (await res.json()) as Contact;
      setContact(updated);
      if (updated.organization_id) {
        fetch(`/api/organizations/${updated.organization_id}`)
          .then((r) => (r.ok ? r.json() : null))
          .then((o) => setOrganization(o as Organization | null))
          .catch(() => setOrganization(null));
      } else {
        setOrganization(null);
      }
      if (!opts?.keepEditing) setEditingSection(null);
      return true;
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Update fehlgeschlagen.');
      return false;
    } finally {
      setSaving(false);
    }
  };

  const isPrivat = contact.contact_kind === 'Privatkunde';

  const executeDeleteContact = async () => {
    if (!id) return;
    setDeleting(true);
    setError(null);
    try {
      const res = await fetch(`/api/contacts/${encodeURIComponent(id)}`, { method: 'DELETE' });
      if (!res.ok) {
        throw new Error(await parseApiError(res, 'Kontakt konnte nicht gelöscht werden.'));
      }
      navigate(contactsHubPath('contacts'));
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Kontakt konnte nicht gelöscht werden.');
      setDeleteConfirmOpen(false);
    } finally {
      setDeleting(false);
    }
  };

  return (
    <div className="h-screen min-h-0 flex overflow-hidden bg-app-bg font-sans text-app-text antialiased">
      <AppSidebar active="contacts" />

      <main className="flex-1 min-h-0 flex flex-col min-w-0 overflow-hidden pb-16 md:pb-0">
        <AppTopBar title="Kontakt" />

        <div className="app-scrollbar flex-1 min-h-0 overflow-y-auto overscroll-contain bg-app-surface md:bg-transparent">
          <div className="max-w-[1180px] w-full mx-auto md:px-6 md:py-6">
          {error && (
            <div className="mx-4 md:mx-0 mt-3 md:mt-0 rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700 mb-3 md:mb-4">{error}</div>
          )}

          <div className="relative md:space-y-4 md:pl-11">
            <button
              type="button"
              onClick={goBack}
              className="hidden md:inline-flex absolute left-0 top-5 lg:top-6 z-10 h-9 w-9 items-center justify-center rounded-md text-app-text-subtle hover:bg-gray-200/60 hover:text-app-text-muted"
              aria-label="Zurück"
            >
              <ArrowLeft className="w-5 h-5" />
            </button>

            <div className="bg-app-surface border-b border-app-border/60 md:border md:border-app-border md:rounded-[10px] md:shadow-[0_1px_2px_rgba(0,0,0,0.03)] px-4 py-4 md:p-5 flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between sm:gap-6">
              <div className="flex items-center gap-3 md:gap-4 min-w-0 flex-1">
                <span
                  className={`w-12 h-12 md:w-16 md:h-16 rounded-full grid place-items-center text-lg md:text-xl font-bold shrink-0 ${contactAvatarClassName(
                    contact.contact_kind,
                  )}`}
                >
                  {contactInitials(contact)}
                </span>
                <div className="flex-1 min-w-0">
                  <div className="flex flex-wrap items-center gap-2">
                    <h1 className="text-lg md:text-[22px] font-semibold text-app-text tracking-tight truncate">
                      {contactDisplayName(contact) || '—'}
                    </h1>
                    {contact.contact_kind && (
                      <ContactKindBadgePicker
                        value={contact.contact_kind}
                        contact={contact}
                        saving={saving}
                        onSwitch={(patch) => handleSave(patch)}
                      />
                    )}
                  </div>
                  <p className="mt-1 text-sm text-app-text-subtle leading-snug">
                    {isPrivat
                      ? (contact.address || '—').trim() || '—'
                      : (() => {
                          const person = [contact.first_name, contact.last_name].filter(Boolean).join(' ').trim();
                          const co = (contact.company ?? '').trim() || organization?.name || '';
                          return [person || contactDisplayName(contact), co].filter(Boolean).join(' · ') || '—';
                        })()}
                  </p>
                  <div className="mt-0.5 flex flex-wrap items-center gap-x-2 gap-y-0.5 text-sm text-app-text-subtle min-w-0 w-full leading-snug">
                    <span className="shrink-0 tabular-nums">{contact.customer_number ?? '—'}</span>
                    {organization && (
                      <>
                        <span className="shrink-0">·</span>
                        <button
                          type="button"
                          onClick={() => navigate(organizationDetailPath(organization.id))}
                          className="inline-flex min-w-0 items-center gap-1 text-blue-600 hover:underline truncate max-w-[14rem] sm:max-w-[18rem]"
                        >
                          <Building2 className="w-3 h-3 shrink-0" /> {organization.name}
                        </button>
                      </>
                    )}
                    {(contact.email || contact.phone) && (
                      <>
                        <span className="shrink-0 hidden sm:inline" aria-hidden>
                          ·
                        </span>
                        <span className="inline-flex items-center gap-0.5 shrink-0 ml-auto sm:ml-0">
                          {contact.email && (
                            <button
                              type="button"
                              onClick={openEmailCompose}
                              disabled={emailComposeBusy}
                              className="inline-flex h-9 w-9 items-center justify-center rounded-md text-app-text-subtle hover:bg-app-hover hover:text-blue-600 disabled:opacity-50"
                              title="E-Mail"
                            >
                              {emailComposeBusy ? (
                                <LoadingSpinner size="sm" />
                              ) : (
                                <Mail className="w-3.5 h-3.5" />
                              )}
                            </button>
                          )}
                          {contact.phone && (
                            <a
                              href={`tel:${contact.phone}`}
                              className="inline-flex h-9 w-9 items-center justify-center rounded-md text-app-text-subtle hover:bg-app-hover hover:text-blue-600"
                              title="Anrufen"
                            >
                              <Phone className="w-3.5 h-3.5" />
                            </a>
                          )}
                        </span>
                      </>
                    )}
                  </div>
                </div>
              </div>
              {projectsEnabled && (
              <div className="shrink-0 flex flex-row sm:flex-col items-baseline sm:items-end justify-between sm:justify-start gap-2 sm:gap-0 text-left sm:text-right border-t border-app-border/60 pt-3 sm:border-t-0 sm:pt-0">
                <span className={FILTER_GROUP_LABEL_CLASS}>Gesamtumsatz</span>
                {contactRevenueTotals.grossSum != null && contactRevenueTotals.netSum != null ? (
                  <div className="flex flex-col items-end">
                    <span className="text-lg md:text-[22px] font-semibold tabular-nums text-app-text tracking-tight leading-tight">
                      {formatCurrency(contactRevenueTotals.grossSum)}
                    </span>
                    <span className="text-sm md:text-sm tabular-nums text-app-text-subtle leading-snug">
                      {formatCurrency(contactRevenueTotals.netSum)}
                    </span>
                  </div>
                ) : contactRevenueTotals.grossSum != null ? (
                  <span className="text-lg md:text-[22px] font-semibold tabular-nums text-app-text tracking-tight leading-tight">
                    {formatCurrency(contactRevenueTotals.grossSum)}
                  </span>
                ) : contactRevenueTotals.netSum != null ? (
                  <span className="text-lg md:text-[22px] font-semibold tabular-nums text-app-text tracking-tight leading-tight">
                    {formatCurrency(contactRevenueTotals.netSum)}
                  </span>
                ) : (
                  <span className="text-lg md:text-[22px] font-semibold tabular-nums text-gray-300 tracking-tight leading-tight">—</span>
                )}
              </div>
              )}
            </div>
            <CustomerPartySummary party={contact} className="mt-3" />

            <div className="grid grid-cols-1 lg:grid-cols-3 md:gap-4">
            <div className="order-2 lg:order-1 lg:col-span-2 md:space-y-4">
              {projectsEnabled && (
                <LeadProjectsSection
                  leads={leads}
                  headerAction={canOpen(projectsListPath()) && (
                    <button
                      type="button"
                      onClick={() => setCreateLeadModalOpen(true)}
                      className="shrink-0 text-xs text-blue-600 hover:underline inline-flex items-center gap-1 font-medium"
                    >
                      <Plus className="w-3.5 h-3.5" /> Projekte
                    </button>
                  )}
                />
              )}

              {invoicesEnabled && (
                <SectionCard
                  title="Angebote &amp; Rechnungen"
                  subtitle={invoices.length > 0 ? `${invoices.length} Dokumente` : ''}
                >
                  {invoices.length === 0 ? (
                    <p className="text-sm text-app-text-subtle">Noch keine versendeten Dokumente.</p>
                  ) : (
                    <InvoiceDocumentsList invoices={invoices} recipientEmail={contact.email} />
                  )}
                </SectionCard>
              )}
            </div>

            <div className="order-1 lg:order-2 md:space-y-4">
              <SectionCard
                title="Kontaktdaten"
                action={
                  <ContactSectionEditToggle
                    section="contact"
                    current={editingSection}
                    onChange={setEditingSection}
                    label="Kontaktdaten"
                  />
                }
              >
                {editingSection === 'contact' ? (
                  <EditContactKontaktdatenForm
                    contact={contact}
                    saving={saving}
                    onSave={handleSave}
                    onCancel={() => setEditingSection(null)}
                  />
                ) : (
                  <>
                    <DataRow label="E-Mail" value={contact.email || '—'} />
                    <DataRow label="Telefon" value={contact.phone || '—'} />
                    <DataRow label="Adresse" value={contact.address || '—'} />
                  </>
                )}
              </SectionCard>

              <SectionCard
                title="Stammdaten"
                action={
                  <ContactSectionEditToggle
                    section="stammdaten"
                    current={editingSection}
                    onChange={setEditingSection}
                    label="Stammdaten"
                  />
                }
              >
                {editingSection === 'stammdaten' ? (
                  <EditContactStammdatenForm
                    contact={contact}
                    organization={organization}
                    isPrivat={isPrivat}
                    saving={saving}
                    onSave={handleSave}
                  />
                ) : (
                  <>
                    <DataRow label="Kundennr." value={contact.customer_number ?? '—'} />
                    <DataRow label="Erstellt" value={formatDateDe(contact.created_at)} />
                    {!isPrivat && (
                      <div className="grid grid-cols-1 gap-0.5 sm:grid-cols-[110px_1fr] sm:gap-3 py-2.5 sm:py-2 sm:items-baseline">
                        <span className={FILTER_GROUP_LABEL_CLASS}>Organisation</span>
                        <div className="min-w-0 text-sm text-app-text leading-snug break-words">
                          {organization ? (
                            <button
                              type="button"
                              onClick={() => navigate(organizationDetailPath(organization.id))}
                              className="text-blue-600 hover:underline text-left"
                            >
                              {organization.name}
                            </button>
                          ) : (
                            (contact.company ?? '').trim() || '—'
                          )}
                        </div>
                      </div>
                    )}
                  </>
                )}
              </SectionCard>

              <SectionCard
                title="Notizen"
                action={
                  <ContactSectionEditToggle
                    section="notes"
                    current={editingSection}
                    onChange={setEditingSection}
                    label="Notizen"
                  />
                }
              >
                {editingSection === 'notes' ? (
                  <EditContactNotizenForm
                    contact={contact}
                    saving={saving}
                    onSave={handleSave}
                    onCancel={() => setEditingSection(null)}
                  />
                ) : contact.notes?.trim() ? (
                  <p className="text-xs text-app-text-muted whitespace-pre-wrap">{contact.notes}</p>
                ) : (
                  <p className="text-sm text-app-text-subtle">Keine Notizen.</p>
                )}
              </SectionCard>

              <div className="px-4 py-4 md:px-0 md:py-0">
                <button
                  type="button"
                  onClick={() => setDeleteConfirmOpen(true)}
                  disabled={deleting}
                  className="app-btn-danger-outline inline-flex h-9 items-center gap-1.5 px-3.5 border border-red-200 rounded-md bg-app-surface text-sm text-red-600 hover:bg-red-50 disabled:opacity-60"
                  aria-label="Kontakt löschen"
                >
                  <Trash2 className="w-3.5 h-3.5 shrink-0" />
                  Kontakt löschen
                </button>
              </div>
            </div>
          </div>
          </div>
          </div>
        </div>

      </main>

      {createLeadModalOpen && (
        <CreateLeadModal
          onClose={() => setCreateLeadModalOpen(false)}
          onCreated={(lead: Lead) => {
            navigate(leadDetailPath(lead));
          }}
          prefillContactId={contact.id}
        />
      )}
      {emailCompose && (
        <ComposeEmailModal
          key={`contact-mail-${emailComposeResetKey}`}
          open
          accountId={emailCompose.accountId}
          resetKey={emailComposeResetKey}
          initialTo={emailCompose.to}
          initialSubject={emailCompose.subject}
          initialBody={emailCompose.body}
          initialFromEmail={emailCompose.fromEmail}
          overlayClassName="z-[260]"
          onClose={() => setEmailCompose(null)}
          onSent={() => setEmailCompose(null)}
        />
      )}

      <AppConfirmDialog
        open={deleteConfirmOpen}
        onClose={() => !deleting && setDeleteConfirmOpen(false)}
        title="Kontakt löschen?"
        description={
          contact ? (
            <>
              Soll der Kontakt{' '}
              <span className="font-semibold text-app-text">
                „{contactDisplayName(contact) || 'Unbenannt'}“
              </span>{' '}
              wirklich gelöscht werden? Er erscheint danach nicht mehr in der Übersicht.
              Bestehende Projekte und Ausschreibungen behalten den Kundennamen.
            </>
          ) : null
        }
        confirmLabel="Löschen"
        cancelLabel="Abbrechen"
        destructive
        isLoading={deleting}
        onConfirm={executeDeleteContact}
      />
    </div>
  );
};

/* ----------------------------- Helpers / UI ------------------------------- */

const SectionCard: React.FC<{
  title: string;
  subtitle?: string;
  action?: React.ReactNode;
  children: React.ReactNode;
}> = ({ title, subtitle, action, children }) => (
  <section className="bg-app-surface border-b border-app-border/60 md:border md:border-app-border md:rounded-lg md:shadow-[0_1px_2px_rgba(0,0,0,0.03)] md:overflow-hidden last:border-b-0 md:last:border">
    <header className="px-4 py-3 min-h-11 border-b border-app-border/60 flex items-center justify-between gap-2">
      <div className="flex items-baseline gap-2 min-w-0">
        <h2 className="text-sm font-semibold text-app-text shrink-0">{title}</h2>
        {subtitle && <span className="text-sm text-app-text-subtle truncate">{subtitle}</span>}
      </div>
      <div className="shrink-0 flex items-center gap-1">{action}</div>
    </header>
    <div className="px-4 py-3 md:p-4">{children}</div>
  </section>
);

const ContactSectionEditToggle: React.FC<{
  section: 'contact' | 'stammdaten' | 'notes';
  current: 'contact' | 'stammdaten' | 'notes' | null;
  onChange: (v: 'contact' | 'stammdaten' | 'notes' | null) => void;
  label: string;
}> = ({ section, current, onChange, label }) =>
  current === section ? (
    <button
      type="button"
      onClick={() => onChange(null)}
      className="shrink-0 inline-flex items-center justify-center rounded text-app-text-subtle hover:bg-app-hover hover:text-app-text-muted"
      aria-label="Bearbeitung abbrechen"
    >
      <X className="w-3.5 h-3.5" />
    </button>
  ) : (
    <button
      type="button"
      onClick={() => onChange(section)}
      disabled={current !== null}
      className="shrink-0 inline-flex items-center justify-center rounded text-gray-400 hover:text-app-text-muted hover:bg-app-hover disabled:opacity-30 disabled:pointer-events-none"
      aria-label={`${label} bearbeiten`}
    >
      <Edit2 className="w-3.5 h-3.5" />
    </button>
  );

const DataRow: React.FC<{ label: string; value: string }> = ({ label, value }) => (
  <div className="grid grid-cols-1 gap-0.5 sm:grid-cols-[110px_1fr] sm:gap-3 py-2.5 sm:py-2 sm:items-baseline">
    <span className={FILTER_GROUP_LABEL_CLASS}>{label}</span>
    <span className="text-sm text-app-text leading-snug break-words">{value || '—'}</span>
  </div>
);

/** Label above field on mobile; side-by-side from `sm` (desktop). */
const EditDataRow: React.FC<{ label: string; alignTop?: boolean; children: React.ReactNode }> = ({
  label,
  alignTop,
  children,
}) => (
  <div
    className={`grid grid-cols-1 gap-1 sm:grid-cols-[110px_1fr] sm:gap-3 py-2.5 sm:py-2 ${
      alignTop ? 'sm:items-start' : 'sm:items-center'
    }`}
  >
    <span
      className={`${FILTER_GROUP_LABEL_CLASS} shrink-0 ${
        alignTop ? 'sm:pt-1.5' : ''
      }`}
    >
      {label}
    </span>
    <div className="min-w-0">{children}</div>
  </div>
);

const formFooter = (onCancel: () => void, saving: boolean) => (
  <div className="flex items-center justify-end gap-2 pt-3 mt-2 border-t border-app-border/60">
    <button type="button" onClick={onCancel} className={APP_BTN_OUTLINE_CLASS}>
      Abbrechen
    </button>
    <button type="submit" disabled={saving} className={APP_BTN_PRIMARY_CLASS}>
      {saving ? 'Speichern …' : 'Speichern'}
    </button>
  </div>
);

const EditContactStammdatenForm: React.FC<{
  contact: Contact;
  organization: Organization | null;
  isPrivat: boolean;
  saving: boolean;
  onSave: (patch: Partial<Contact>, opts?: { keepEditing?: boolean }) => void | boolean | Promise<void | boolean>;
}> = ({ contact, organization, isPrivat, saving, onSave }) => {
  const [customerNumber, setCustomerNumber] = React.useState(contact.customer_number ?? '');
  React.useEffect(() => setCustomerNumber(contact.customer_number ?? ''), [contact.customer_number]);

  const canEditCustomerNumber = contact.numbering_manual_edit_enabled !== false;

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!canEditCustomerNumber) return;
    void onSave({ customer_number: customerNumber.trim() });
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-0">
      <EditDataRow label="Kundennr.">
        {canEditCustomerNumber ? (
          <input
            type="text"
            value={customerNumber}
            onChange={(e) => setCustomerNumber(e.target.value)}
            className="w-full h-9 px-3 py-0 bg-app-surface border border-app-border rounded-md text-sm font-mono tabular-nums"
            spellCheck={false}
            autoComplete="off"
          />
        ) : (
          <span className="text-sm font-mono tabular-nums text-app-text">
            {contact.customer_number ?? '—'}
          </span>
        )}
      </EditDataRow>
      <EditDataRow label="Erstellt">
        <span className="text-sm text-app-text-muted tabular-nums">{formatDateDe(contact.created_at)}</span>
      </EditDataRow>
      {!isPrivat && (
        <EditDataRow label="Organisation" alignTop>
          <OrganizationAssignSelect
            valueId={contact.organization_id ? String(contact.organization_id) : null}
            valueLabel={organization?.name?.trim() || (contact.company ?? '').trim() || ''}
            saving={saving}
            onSelect={async (org) => {
              await onSave({ organization_id: org.id }, { keepEditing: true });
            }}
          />
        </EditDataRow>
      )}
      {canEditCustomerNumber && (
        <div className="flex justify-end gap-2 pt-3">
          <button
            type="submit"
            disabled={saving || !customerNumber.trim() || customerNumber.trim() === (contact.customer_number ?? '')}
            className={APP_BTN_PRIMARY_CLASS}
          >
            {saving ? 'Speichern …' : 'Speichern'}
          </button>
        </div>
      )}
    </form>
  );
};

const EditContactKontaktdatenForm: React.FC<{
  contact: Contact;
  saving: boolean;
  onSave: (patch: Partial<Contact>) => void | boolean | Promise<void | boolean>;
  onCancel: () => void;
}> = ({ contact, saving, onSave, onCancel }) => {
  const [form, setForm] = React.useState<Contact>(contact);
  React.useEffect(() => setForm(contact), [contact]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    void onSave({
      first_name: form.first_name ?? null,
      last_name: form.last_name ?? null,
      email: form.email ?? '',
      phone: form.phone ?? '',
      address: form.address ?? '',
    });
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-0">
      <EditDataRow label="Vorname">
        <input
          type="text"
          value={form.first_name ?? ''}
          onChange={(e) => setForm((prev) => ({ ...prev, first_name: e.target.value }))}
          className="w-full h-9 px-3 py-0 bg-app-surface border border-app-border rounded-md text-sm"
        />
      </EditDataRow>
      <EditDataRow label="Nachname">
        <input
          type="text"
          value={form.last_name ?? ''}
          onChange={(e) => setForm((prev) => ({ ...prev, last_name: e.target.value }))}
          className="w-full h-9 px-3 py-0 bg-app-surface border border-app-border rounded-md text-sm"
        />
      </EditDataRow>
      <EditDataRow label="E-Mail">
        <input
          type="email"
          value={form.email ?? ''}
          onChange={(e) => setForm((prev) => ({ ...prev, email: e.target.value }))}
          className="w-full h-9 px-3 py-0 bg-app-surface border border-app-border rounded-md text-sm"
        />
      </EditDataRow>
      <EditDataRow label="Telefon">
        <input
          type="tel"
          value={form.phone ?? ''}
          onChange={(e) => setForm((prev) => ({ ...prev, phone: e.target.value }))}
          className="w-full h-9 px-3 py-0 bg-app-surface border border-app-border rounded-md text-sm"
        />
      </EditDataRow>
      <EditDataRow label="Adresse">
        <AddressAutocompleteInput
          value={form.address ?? ''}
          onChange={(v) => setForm((prev) => ({ ...prev, address: v }))}
          inputClassName="w-full h-9 px-3 py-0 bg-app-surface border border-app-border rounded-md text-sm"
        />
      </EditDataRow>
      {formFooter(onCancel, saving)}
    </form>
  );
};

const EditContactNotizenForm: React.FC<{
  contact: Contact;
  saving: boolean;
  onSave: (patch: Partial<Contact>) => void | boolean | Promise<void | boolean>;
  onCancel: () => void;
}> = ({ contact, saving, onSave, onCancel }) => {
  const [notes, setNotes] = React.useState(contact.notes ?? '');
  React.useEffect(() => setNotes(contact.notes ?? ''), [contact.notes]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    void onSave({ notes: notes || null });
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-0">
      <EditDataRow label="Notizen" alignTop>
        <textarea
          rows={5}
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          className="w-full px-3 py-2 bg-app-surface border border-app-border rounded-md text-sm resize-y min-h-[5rem]"
        />
      </EditDataRow>
      {formFooter(onCancel, saving)}
    </form>
  );
};
